import { supabase } from '../lib/supabase';

export interface ProposalPhoto {
  id: string;
  proposal_id: string;
  file_name: string;
  file_path: string;
  file_size: number | null;
  mime_type: string | null;
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface UploadProposalPhotoParams {
  proposalId: string;
  file: File;
}

export interface UploadProposalPhotoResult {
  success: boolean;
  photo?: ProposalPhoto;
  error?: string;
}

/**
 * Upload a photo for a proposal
 */
export async function uploadProposalPhoto({
  proposalId,
  file
}: UploadProposalPhotoParams): Promise<UploadProposalPhotoResult> {
  try {
    console.log('📸 Starting proposal photo upload:', { proposalId, fileName: file.name });

    // Generate unique file name
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `proposal-photos/${proposalId}/${fileName}`;

    console.log('📸 Generated file path:', filePath);

    // Upload file to Supabase storage
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('user-uploads') // Use the established user-uploads bucket
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      console.error('❌ Storage upload error:', uploadError);
      return { success: false, error: `Upload failed: ${uploadError.message}` };
    }

    console.log('✅ File uploaded to storage:', uploadData.path);

    // Get current user for uploaded_by
    const { data: { user } } = await supabase.auth.getUser();

    // Save photo record to database
    console.log('📸 Inserting proposal photo record:', { 
      proposalId, 
      fileName: file.name 
    });

    const { data: photoData, error: dbError } = await supabase
      .from('proposal_photos')
      .insert({
        proposal_id: proposalId,
        file_name: file.name,
        file_path: filePath,
        file_size: file.size,
        mime_type: file.type,
        uploaded_by: user?.id || null
      })
      .select()
      .single();

    if (dbError) {
      console.error('❌ Database insert error:', dbError);
      // Try to clean up uploaded file
      await supabase.storage
        .from('user-uploads')
        .remove([filePath]);
      return { success: false, error: `Failed to save photo record: ${dbError.message}` };
    }

    console.log('✅ Proposal photo uploaded successfully:', photoData);
    return { success: true, photo: photoData };
  } catch (error) {
    console.error('❌ Unexpected error uploading proposal photo:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
}

/**
 * Get all photos for a proposal
 */
export async function getProposalPhotos(proposalId: string): Promise<ProposalPhoto[]> {
  try {
    const { data, error } = await supabase
      .from('proposal_photos')
      .select('*')
      .eq('proposal_id', proposalId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ Error fetching proposal photos:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('❌ Unexpected error fetching proposal photos:', error);
    return [];
  }
}

/**
 * Delete a proposal photo
 */
export async function deleteProposalPhoto(photoId: string): Promise<boolean> {
  try {
    // Get photo record to find file path
    const { data: photo, error: fetchError } = await supabase
      .from('proposal_photos')
      .select('file_path')
      .eq('id', photoId)
      .single();

    if (fetchError || !photo) {
      console.error('❌ Error fetching photo for deletion:', fetchError);
      return false;
    }

    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from('user-uploads')
      .remove([photo.file_path]);

    if (storageError) {
      console.error('❌ Error deleting photo from storage:', storageError);
      // Continue to delete from database anyway
    }

    // Delete from database
    const { error: dbError } = await supabase
      .from('proposal_photos')
      .delete()
      .eq('id', photoId);

    if (dbError) {
      console.error('❌ Error deleting photo from database:', dbError);
      return false;
    }

    return true;
  } catch (error) {
    console.error('❌ Unexpected error deleting proposal photo:', error);
    return false;
  }
}

